import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useErrand } from '../contexts/ErrandContext';
import { useAuth } from '../contexts/AuthContext';
import { showSuccess, showError, showWarning } from '../utils/toast';
import { getErrorMessage } from '../utils/errorMessages';
import GPSLocationPicker from '../components/Map/GPSLocationPicker';
import StashitLogo from '../components/Logo/StashitLogo';
import { MultiStopErrandForm } from '../components/MultiStopErrandForm';
import PricingCalculator from '../components/PricingCalculator';
import { API_ENDPOINTS } from '../config/api';
import { reverseGeocode } from '../utils/geocoding';

// Helper function to calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in kilometers
};

const ErrandRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    errandTypes,
    createErrand,
    fetchUserErrands,
    loading,
    error,
    clearError
  } = useErrand();

  const [formData, setFormData] = useState({
    errandTypeId: '',
    title: '',
    description: '',
    pickupLocation: '',
    pickupLatitude: 0,
    pickupLongitude: 0,
    dropoffLocation: '',
    dropoffLatitude: 0,
    dropoffLongitude: 0,
    estimatedValue: '',
    preferredTimeSlot: 'now' as 'now' | 'schedule',
    scheduledTime: '',
    paymentMethod: 'cash_on_delivery' as 'wallet' | 'cash_on_delivery' | 'admin_escrow',
    recipientContact: '',
    specialInstructions: '',
    insuranceOpted: false,
    insuranceAmount: ''
  });

  const [showDropoff, setShowDropoff] = useState(false);
  const [showScheduledTime, setShowScheduledTime] = useState(false);
  const [showInsurance, setShowInsurance] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showDropoffMap, setShowDropoffMap] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState<{
    totalCost: number;
    serviceFee: number;
    driverEarnings: number;
    basePrice: number;
    demandMultiplier: number;
    demandLevel: string;
    distance: number;
    isOutOfTown: boolean;
    distanceCost: number;
  } | null>(null);
  const [demandData, setDemandData] = useState<{
    errands_pending_count: number;
    drivers_available_count: number;
    demand_ratio: number;
    pricing_multiplier: number;
    demand_level: string;
  } | null>(null);
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isMultiStopMode, setIsMultiStopMode] = useState(false);

  // Form progress tracking with improved accuracy
  const [formProgress, setFormProgress] = useState({
    errandType: false,
    basicInfo: false,
    locations: false,
    scheduling: false,
    payment: false
  });

  // Service area validation
  const GABORONE_CENTER = { latitude: -24.6282, longitude: 25.9231 };
  const SERVICE_RADIUS_KM = 50; // 50km service radius

  // Location validation function
  const validateLocationInServiceArea = (lat: number, lng: number): boolean => {
    const distance = calculateDistance(
      GABORONE_CENTER.latitude,
      GABORONE_CENTER.longitude,
      lat,
      lng
    );
    return distance <= SERVICE_RADIUS_KM;
  };

  // Offline support - store errand drafts
  const storeOfflineErrand = async (errandData: any) => {
    try {
      const offlineErrands = JSON.parse(localStorage.getItem('offlineErrands') || '[]');
      const offlineErrand = {
        id: Date.now().toString(),
        data: errandData,
        createdAt: new Date().toISOString(),
        synced: false
      };
      offlineErrands.push(offlineErrand);
      localStorage.setItem('offlineErrands', JSON.stringify(offlineErrands));
    } catch (error) {
      console.error('Failed to store offline errand:', error);
    }
  };

  // Sync offline errands when back online
  const syncOfflineErrands = async () => {
    try {
      const offlineErrands = JSON.parse(localStorage.getItem('offlineErrands') || '[]');
      if (offlineErrands.length === 0) return;

      const unsyncedErrands = offlineErrands.filter((errand: any) => !errand.synced);

      for (const offlineErrand of unsyncedErrands) {
        try {
          const result = await createErrand(offlineErrand.data);
          if (result) {
            offlineErrand.synced = true;
            showSuccess('Offline errand submitted successfully!');
          }
        } catch (error) {
          console.error('Failed to sync offline errand:', error);
        }
      }

      // Update localStorage with synced errands
      localStorage.setItem('offlineErrands', JSON.stringify(offlineErrands));
    } catch (error) {
      console.error('Failed to sync offline errands:', error);
    }
  };

  // Update form progress based on filled fields with improved logic
  useEffect(() => {
    const selectedErrandType = errandTypes.find(type => type.id === formData.errandTypeId);

    setFormProgress({
      errandType: !!formData.errandTypeId,
      basicInfo: !!formData.title && formData.title.length >= 3,
      locations: (
        // Pickup location is required for all errands
        !!formData.pickupLocation && formData.pickupLatitude !== 0
      ) && (
        // Dropoff location is required only for errands that need it
        !showDropoff || (!!formData.dropoffLocation && formData.dropoffLatitude !== 0)
      ),
      scheduling: !!formData.preferredTimeSlot && (
        formData.preferredTimeSlot === 'now' ||
        (formData.preferredTimeSlot === 'schedule' && !!formData.scheduledTime)
      ),
      payment: !!formData.paymentMethod
    });
  }, [formData, showDropoff, errandTypes]);

  // Update form when errand type changes
  useEffect(() => {
    if (formData.errandTypeId) {
      const selectedType = errandTypes.find(type => type.id === formData.errandTypeId);
      if (selectedType) {
        setShowDropoff(selectedType.name !== 'Pay a Bill' && selectedType.name !== 'Send Money');
        setShowInsurance(selectedType.requires_verification);
      }
    }
  }, [formData.errandTypeId, errandTypes]);

  // Update scheduled time field visibility
  useEffect(() => {
    setShowScheduledTime(formData.preferredTimeSlot === 'schedule');
  }, [formData.preferredTimeSlot]);

  // Get user's current location on mount with reverse geocoding
  useEffect(() => {
    const getCurrentLocation = async () => {
      if (navigator.geolocation) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 300000 // 5 minutes
            });
          });

          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          setLocationPermissionGranted(true);
          setLocationError(null);

          // Auto-fill pickup location if not set
          if (!formData.pickupLocation && !formData.pickupLatitude) {
            try {
              // Reverse geocode to get human-readable address
              const address = await reverseGeocode(latitude, longitude);
              if (address) {
                setFormData(prev => ({
                  ...prev,
                  pickupLocation: address,
                  pickupLatitude: latitude,
                  pickupLongitude: longitude
                }));
              } else {
                // Fallback to coordinates if reverse geocoding fails
                setFormData(prev => ({
                  ...prev,
                  pickupLocation: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
                  pickupLatitude: latitude,
                  pickupLongitude: longitude
                }));
              }
            } catch (geocodeError) {
              console.error('Reverse geocoding failed:', geocodeError);
              // Still set coordinates even if geocoding fails
              setFormData(prev => ({
                ...prev,
                pickupLatitude: latitude,
                pickupLongitude: longitude
              }));
            }
          }
        } catch (error: any) {
          console.log('Geolocation error:', error);
          setLocationPermissionGranted(false);

          if (error.code === error.PERMISSION_DENIED) {
            setLocationError('Location access denied. You can still enter locations manually.');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            setLocationError('Location unavailable. Please check your GPS settings.');
          } else if (error.code === error.TIMEOUT) {
            setLocationError('Location request timed out. Please try again.');
          } else {
            setLocationError('Unable to get your location. You can enter addresses manually.');
          }
        }
      } else {
        setLocationError('Geolocation is not supported by your browser.');
      }
    };

    getCurrentLocation();
  }, []);

  // Fetch demand data on component mount
  useEffect(() => {
    const fetchDemandData = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.BASE}/api/analytics/demand`);
        if (response.ok) {
          const data = await response.json();
          console.log('Demand data fetched:', data);
          setDemandData(data);
        } else {
          console.error('Demand API error:', response.status, response.statusText);
          // Set default values if fetch fails
          setDemandData({
            errands_pending_count: 0,
            drivers_available_count: 0,
            demand_ratio: 2.0,
            pricing_multiplier: 1.0,
            demand_level: 'normal'
          });
        }
      } catch (error) {
        console.error('Error fetching demand data:', error);
        // Set default values if fetch fails
        setDemandData({
          errands_pending_count: 0,
          drivers_available_count: 0,
          demand_ratio: 2.0,
          pricing_multiplier: 1.0,
          demand_level: 'normal'
        });
      }
    };
    fetchDemandData();
    // Refresh demand data every 30 seconds
    const interval = setInterval(fetchDemandData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Sync offline errands when coming back online
  useEffect(() => {
    const handleOnline = () => {
      console.log('User came back online, syncing offline errands...');
      syncOfflineErrands();
    };

    const handleOffline = () => {
      console.log('User went offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Try to sync on mount in case there are pending offline errands
    if (navigator.onLine) {
      syncOfflineErrands();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle location selection with map integration
  const handleLocationSelect = (type: 'pickup' | 'dropoff', address: string | null, lat: number, lng: number) => {
    const locationAddress = address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    if (type === 'pickup') {
      setFormData(prev => ({
        ...prev,
        pickupLocation: locationAddress,
        pickupLatitude: lat,
        pickupLongitude: lng
      }));
      setShowMap(false);
    } else {
      setFormData(prev => ({
        ...prev,
        dropoffLocation: locationAddress,
        dropoffLatitude: lat,
        dropoffLongitude: lng
      }));
      setShowDropoffMap(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // Enhanced validation with specific error messages
    const validationErrors: string[] = [];

    if (!formData.errandTypeId) {
      validationErrors.push('Please select an errand type from the dropdown menu.');
    }

    if (!formData.title || formData.title.trim().length < 3) {
      validationErrors.push('Please provide a clear title (minimum 3 characters) describing your errand.');
    }

    if (!formData.pickupLocation || !formData.pickupLatitude || !formData.pickupLongitude) {
      validationErrors.push('Please set your pickup location using the location field or GPS.');
    }

    if (formData.preferredTimeSlot === 'schedule' && !formData.scheduledTime) {
      validationErrors.push('Please select a specific date and time for your scheduled errand.');
    }

    if (showDropoff && (!formData.dropoffLocation || !formData.dropoffLatitude || !formData.dropoffLongitude)) {
      validationErrors.push('Please set a drop-off location for this type of errand.');
    }

    // Validate service area for pickup location
    if (formData.pickupLatitude && formData.pickupLongitude) {
      if (!validateLocationInServiceArea(formData.pickupLatitude, formData.pickupLongitude)) {
        validationErrors.push('Your pickup location is outside our service area (50km radius from Gaborone). Please choose a location within our coverage area.');
      }
    }

    // Validate service area for dropoff location if provided
    if (formData.dropoffLatitude && formData.dropoffLongitude) {
      if (!validateLocationInServiceArea(formData.dropoffLatitude, formData.dropoffLongitude)) {
        validationErrors.push('Your drop-off location is outside our service area (50km radius from Gaborone). Please choose a location within our coverage area.');
      }
    }

    // Validate estimated value for high-value items
    if (formData.estimatedValue && parseFloat(formData.estimatedValue) > 5000) {
      validationErrors.push('For items valued over P5,000, please contact support for special arrangements.');
    }

    // Show validation errors
    if (validationErrors.length > 0) {
      showError(validationErrors.join('\n\n'));
      return;
    }

    console.log('About to create errand with data:', {
      errandTypeId: formData.errandTypeId,
      title: formData.title,
      pickupLocation: formData.pickupLocation
    });

    const errandData = {
      errandTypeId: formData.errandTypeId,
      title: formData.title,
      description: formData.description || undefined,
      pickupLocation: formData.pickupLocation,
      pickupLatitude: formData.pickupLatitude || undefined,
      pickupLongitude: formData.pickupLongitude || undefined,
      dropoffLocation: formData.dropoffLocation || undefined,
      dropoffLatitude: formData.dropoffLatitude || undefined,
      dropoffLongitude: formData.dropoffLongitude || undefined,
      estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : undefined,
      preferredTimeSlot: formData.preferredTimeSlot,
      scheduledTime: formData.scheduledTime || undefined,
      paymentMethod: formData.paymentMethod,
      recipientContact: formData.recipientContact || undefined,
      specialInstructions: formData.specialInstructions || undefined,
      insuranceOpted: formData.insuranceOpted,
      insuranceAmount: formData.insuranceAmount ? parseFloat(formData.insuranceAmount) : undefined
    };

    // Check if user is online
    if (!navigator.onLine) {
      // Store errand for offline submission
      await storeOfflineErrand(errandData);
      showSuccess('Errand saved offline - will be submitted when you\'re back online!');
      navigate('/errands');
      return;
    }

    try {
      console.log('Calling createErrand with:', errandData);
      const result = await createErrand(errandData);
      console.log('createErrand result:', result);

      if (result) {
        // Refresh user errands to show the new one
        await fetchUserErrands();
        showSuccess('Errand request submitted successfully! Drivers will be notified.');
        navigate('/errands'); // Redirect to errands page to see the new errand
      } else {
        console.log('createErrand returned null/undefined');
        showError('Failed to create errand. Please try again.');
      }
    } catch (error) {
      console.error('Error creating errand:', error);

      // If it's a network error, suggest offline storage
      if (!navigator.onLine || error instanceof TypeError) {
        await storeOfflineErrand(errandData);
        showWarning('Network error - errand saved offline and will be submitted when connection is restored.');
        navigate('/errands');
      } else {
        showError(getErrorMessage(error, 'Failed to create errand. Please try again.'));
      }
    }
  };

  const getErrandTypeIcon = (name: string) => {
    switch (name) {
      case 'Pay a Bill': return '💳';
      case 'Send Money': return '💰';
      case 'Receive Money': return '📥';
      case 'Pick up Groceries': return '🛒';
      case 'Drop off Package': return '📦';
      case 'Collect Document': return '📄';
      default: return '✅';
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-neutral-800 mb-4">Please log in to request errands</h1>
          <button
            onClick={() => navigate('/auth/login')}
            className="btn-primary"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <StashitLogo width={200} height={60} showTagline={false} />
        </div>
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          Run an Errand
        </h1>
        <p className="text-neutral-600">
          Let our trusted drivers handle your tasks and deliveries
        </p>

        {/* Errand Type Toggle */}
        <div className="mt-6 flex justify-center">
          <div className="bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setIsMultiStopMode(false)}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                !isMultiStopMode
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Single Stop
            </button>
            <button
              onClick={() => setIsMultiStopMode(true)}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                isMultiStopMode
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Multi-Stop
            </button>
          </div>
        </div>

        {isMultiStopMode && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-2xl mx-auto">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Multi-Stop Errands</p>
                <p>Perfect for complex tasks requiring multiple stops, such as grocery shopping with deliveries, collecting documents from various locations, or running multiple errands in sequence.</p>
              </div>
            </div>
          </div>
        )}
      </div> 
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={clearError}
            className="text-red-600 hover:text-red-800 text-sm mt-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Errand Request Form */}
      {isMultiStopMode ? (
        <MultiStopErrandForm
          onSuccess={() => navigate('/errands')}
          onCancel={() => setIsMultiStopMode(false)}
        />
      ) : (
        <div className="bg-white rounded-lg shadow-medium p-4 md:p-6 border border-neutral-200">
          {/* Form Progress Indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Form Progress</span>
              <span className="text-sm text-gray-500">
                {Object.values(formProgress).filter(Boolean).length}/5 completed
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${(Object.values(formProgress).filter(Boolean).length / 5) * 100}%`
                }}
              ></div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span className={formProgress.errandType ? 'text-green-600 font-medium' : ''}>
                {formProgress.errandType ? '✓' : '○'} Type
              </span>
              <span className={formProgress.basicInfo ? 'text-green-600 font-medium' : ''}>
                {formProgress.basicInfo ? '✓' : '○'} Details
              </span>
              <span className={formProgress.locations ? 'text-green-600 font-medium' : ''}>
                {formProgress.locations ? '✓' : '○'} Locations
              </span>
              <span className={formProgress.scheduling ? 'text-green-600 font-medium' : ''}>
                {formProgress.scheduling ? '✓' : '○'} Time
              </span>
              <span className={formProgress.payment ? 'text-green-600 font-medium' : ''}>
                {formProgress.payment ? '✓' : '○'} Payment
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            {/* Errand Type Selection */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                What do you need done? *
              </label>
              <select
                name="errandTypeId"
                value={formData.errandTypeId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              >
                <option value="">Select an errand type</option>
                {errandTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {getErrandTypeIcon(type.name)} {type.name} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Title/Description *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Pay electricity bill at Mall"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>

            {/* Detailed Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Additional Details
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Provide more details about the errand..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Pickup Location */}
            <GPSLocationPicker
              onLocationSelect={(address, lat, lng) => {
                setFormData(prev => ({
                  ...prev,
                  pickupLocation: address,
                  pickupLatitude: lat,
                  pickupLongitude: lng
                }));
              }}
              initialAddress={formData.pickupLocation}
              initialLat={formData.pickupLatitude || undefined}
              initialLng={formData.pickupLongitude || undefined}
              label="Pickup Location"
              required
            />

            {/* Drop-off Location */}
            {showDropoff && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Drop-off Location *
                </label>
                <input
                  type="text"
                  name="dropoffLocation"
                  value={formData.dropoffLocation}
                  onChange={handleInputChange}
                  placeholder="Where should the driver deliver?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required={showDropoff}
                />
                <button
                  type="button"
                  onClick={() => setShowDropoffMap(true)}
                  className="mt-2 text-sm text-green-600 hover:text-green-700"
                >
                  📍 Select on map
                </button>
              </div>
            )}

            {/* Time Preference */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                When do you need this done? *
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="preferredTimeSlot"
                    value="now"
                    checked={formData.preferredTimeSlot === 'now'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-600 focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-neutral-700">As soon as possible</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="preferredTimeSlot"
                    value="schedule"
                    checked={formData.preferredTimeSlot === 'schedule'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-600 focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-neutral-700">Schedule for later</span>
                </label>
              </div>
            </div>

            {/* Scheduled Time */}
            {showScheduledTime && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Scheduled Time *
                </label>
                <input
                  type="datetime-local"
                  name="scheduledTime"
                  value={formData.scheduledTime}
                  onChange={handleInputChange}
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required={showScheduledTime}
                />
              </div>
            )}

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Payment Method *
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="cash_on_delivery"
                    checked={formData.paymentMethod === 'cash_on_delivery'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-600 focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-neutral-700">💰 Pay cash to driver</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="wallet"
                    checked={formData.paymentMethod === 'wallet'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-green-600 focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-neutral-700">💳 Pay from wallet</span>
                </label>
              </div>
            </div>

            {/* Special Instructions */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Special Instructions
              </label>
              <textarea
                name="specialInstructions"
                value={formData.specialInstructions}
                onChange={handleInputChange}
                placeholder="Any special instructions for the driver..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Pricing Calculator */}
            <PricingCalculator
              errandType={errandTypes.find(type => type.id === formData.errandTypeId) || null}
              demandData={demandData}
              pickupCoords={formData.pickupLatitude && formData.pickupLongitude ? {
                lat: formData.pickupLatitude,
                lng: formData.pickupLongitude
              } : undefined}
              dropoffCoords={formData.dropoffLatitude && formData.dropoffLongitude ? {
                lat: formData.dropoffLatitude,
                lng: formData.dropoffLongitude
              } : undefined}
            />

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg"
              >
                {loading ? 'Submitting...' : 'Request Errand'}
              </button>
            </div>
          </form>
        </div>
      )}


    </div>
  );
};

export default ErrandRequestPage;
